import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, BookOpen, Target, TrendingUp, ClipboardList, BarChart3, User, ChevronRight, Bell } from 'lucide-react';
import { Card } from './ui/card';
import LoadingAnimation from './LoadingAnimation';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Relatorio from './Relatorio';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIMENSION_LABELS = {
  voz: 'Expressão Autêntica',
  travessia: 'Gestão de Conflitos',
  receptividade: 'Ambientes de Confiança',
  tomada_perspectiva: 'Tomada de Perspectiva',
  cuidado_compassivo: 'Cuidado Compassivo',
  presenca_empatica: 'Presença Empática',
  pertencimento_vinculo: 'Pertencimento e Vínculo',
  confianca_relacional: 'Confiança Relacional',
  solidao: 'Conexão e Solidão',
  seguranca_discordar: 'Segurança para Discordar',
};

const PDIPage = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [perfil, setPerfil] = useState(null);
  const [hasPerfil, setHasPerfil] = useState(false);
  const [pdi, setPdi] = useState(null);
  const [activeWindows, setActiveWindows] = useState([]); // janelas de instrumentos abertas p/ o usuário
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/auth/perfil-relacional`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/instruments/pdi`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/instruments/active`, { withCredentials: true }).catch(() => null),
    ]).then(([perfilRes, pdiRes, activeRes]) => {
      if (cancelled) return;
      if (perfilRes?.data?.has_perfil) {
        setHasPerfil(true);
        setPerfil(perfilRes.data);
      }
      if (pdiRes?.data?.has_pdi) {
        setPdi(pdiRes.data);
      }
      setActiveWindows(activeRes?.data?.instruments || []);
    }).finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Tipos de instrumento com janela aberta agora (ex.: {"IQR","IEO"})
  const openTypes = new Set(activeWindows.map(w => (w.instrument_type || '').toUpperCase()));

  const goToLesson = (rec) => {
    if (rec?.lesson_id) navigate(`/lesson-modules/${rec.lesson_id}`);
    else navigate('/dashboard');
  };

  if (isLoading || !authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-white">
        <LoadingAnimation size="md" />
      </div>
    );
  }

  const nada = !hasPerfil && !pdi?.has_pdi;

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Painel do Colaborador
        </p>

        <Card className="p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Olá, {authUser?.name?.split(' ')[0] || 'Estudante'}
          </h1>
          <p className="text-gray-600 text-sm mt-1">Seu Plano de Desenvolvimento Individual</p>
        </Card>

        {/* Sinalização: janela de indicadores aberta para responder agora */}
        {activeWindows.length > 0 && (
          <Card className="p-5 mb-6 border-2 border-amber-300 bg-amber-50">
            <div className="flex items-start gap-3">
              <Bell className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900">
                  Você tem {activeWindows.length === 1 ? 'um questionário' : `${activeWindows.length} questionários`} para responder
                </h3>
                <p className="text-sm text-amber-800 mt-0.5">
                  Há uma janela de coleta aberta. Responda enquanto está disponível — leva poucos minutos.
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {activeWindows.map(w => (
                    <button
                      key={w.window_id}
                      onClick={() => navigate(`/survey/${(w.instrument_type || '').toLowerCase()}`)}
                      className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
                    >
                      Responder {w.instrument_type}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Avaliações e questionários — sempre disponíveis, por escolha da usuária */}
        <Card className="p-6 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-bold text-gray-900">Avaliações e questionários</h2>
          </div>
          <p className="text-sm text-gray-500 mb-5">
            Faça quando quiser — os resultados ficam registrados aqui.
          </p>

          <div className="space-y-3">
            {[
              { icon: User, title: 'Personalização', desc: 'Conte um pouco sobre você e seu contexto', to: '/personalizacao' },
              { icon: Sparkles, title: 'Perfil Relacional', desc: 'Sua avaliação de competências relacionais', to: '/assessment' },
              { icon: BarChart3, title: 'Indicador IQR', desc: 'Qualidade Relacional', to: '/survey/iqr', type: 'IQR' },
              { icon: BarChart3, title: 'Indicador IEO', desc: 'Empatia Organizacional', to: '/survey/ieo', type: 'IEO' },
              { icon: BarChart3, title: 'Indicador IPR', desc: 'Percepção Relacional', to: '/survey/ipr', type: 'IPR' },
            ].map(({ icon: Icon, title, desc, to, type }) => {
              const open = type && openTypes.has(type);
              return (
                <button
                  key={to}
                  onClick={() => navigate(to)}
                  className={`w-full text-left flex items-center gap-3 bg-white border-2 rounded-2xl p-4 hover:shadow-md transition-all group ${open ? 'border-amber-300' : 'border-gray-200 hover:border-purple-300'}`}
                >
                  <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 group-hover:text-purple-700">{title}</h3>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                  {open && (
                    <span className="text-[11px] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-lg flex-shrink-0">
                      Aberto
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-purple-400 flex-shrink-0" />
                </button>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 mt-4">
            Os indicadores (IQR, IEO, IPR) só abrem quando há uma janela de coleta ativa, definida pelo gestor/admin.
          </p>
        </Card>

        {/* Dica quando ainda não há nenhum resultado */}
        {nada && (
          <Card className="p-6 text-center mb-6">
            <Sparkles className="w-9 h-9 text-purple-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Você ainda não tem resultados. Comece pela <strong>Personalização</strong> ou pelo
              <strong> Perfil Relacional</strong> acima — eles aparecem aqui assim que concluídos.
            </p>
          </Card>
        )}

        {/* Perfil Relacional do onboarding */}
        {hasPerfil && (
          <div className="mb-8">
            <Relatorio
              result={perfil}
              nome={perfil.nome || authUser?.name || ''}
              intencao={perfil.intencao || ''}
            />
          </div>
        )}

        {/* Habilidades prioritárias dos instrumentos IQR/IEO/IPR */}
        {pdi?.has_pdi && pdi.recommendations?.length > 0 && (
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-5 h-5 text-purple-600" />
              <h2 className="text-lg font-bold text-gray-900">Habilidades prioritárias</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Baseadas nos seus resultados nos instrumentos de medição (IQR, IEO, IPR).
            </p>

            <div className="space-y-3">
              {pdi.recommendations.map((rec, idx) => (
                <button
                  key={idx}
                  onClick={() => goToLesson(rec)}
                  className="w-full text-left bg-white border-2 border-gray-200 rounded-2xl p-5 hover:border-purple-300 hover:shadow-md transition-all group"
                  style={{ borderLeftColor: '#7c3aed', borderLeftWidth: 4 }}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ background: '#7c3aed' }}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-purple-700">
                        {rec.skill}
                      </h3>
                      {rec.description && (
                        <p className="text-sm text-gray-600 mb-2">{rec.description}</p>
                      )}
                      <div className="flex items-center gap-3 flex-wrap mt-2">
                        {rec.lesson && (
                          <div className="flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 px-2.5 py-1 rounded-lg">
                            <BookOpen className="w-3.5 h-3.5" />
                            <span>{rec.lesson}</span>
                          </div>
                        )}
                        {rec.dimension && (
                          <span className="text-xs text-gray-400">
                            {DIMENSION_LABELS[rec.dimension] || rec.dimension}
                          </span>
                        )}
                      </div>
                    </div>
                    <TrendingUp className="w-4 h-4 text-gray-300 group-hover:text-purple-400 flex-shrink-0 mt-1 transition-colors" />
                  </div>
                </button>
              ))}
            </div>

            {pdi.generated_at && (
              <p className="text-xs text-gray-400 pt-4 text-center">
                Gerado em {new Date(pdi.generated_at).toLocaleDateString('pt-BR')}
              </p>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default PDIPage;
